// helm-console client entry — the Home Screen shell (spec UI-1: its own
// small client, NOT the Bridge client; no rail, no tabstrip). T1.8 builds
// the real AG-Grid releases list on this mount; T1.1 ships the shell and
// proves the client bundle consumes the shared pi-stack package (the RM
// chat UI reuses pi-live/pi-view per BR1 — mounted by WS3/WS4's tasks).

import { configurePiViewHost } from 'pi-stack/client/host.js';
import { defaultTerminalPrefs } from 'pi-stack/client/term-types.js';
import { mountAssistantOverlay } from 'pi-stack/client/assistant-overlay.js';
import { mountHome } from './home.js';
import { mountUsers } from './users.js';
import { mountAudit } from './audit.js';
import { mountBrain } from './brain.js';
import { mountConfigTemplates } from './config-templates.js';
import { mountTokens } from './tokens.js';
import { currentTheme, hydrate, startAppearanceSync, onAppearanceChange } from './appearance.js';
import { injectIconSprite } from './generated/icons.js';
import { bootStyleSubstrate, describeGround, groundStyle } from './style-substrate.js';
import { installTooltips, resolveRichTip } from './tooltip.js';
import { installConfirmDestructive } from './confirm.js';
import { attachCtxMenu, mountCtxMenu } from './ctxmenu.js';
import { installScrollRails } from 'pi-stack/client/scroll-rails.js';
import { CONSOLE_RAILS } from './rail-registry.js';
import { bootOnboarding } from './onboarding-flows.js';

// The console's RM chat views read theme through the pi-stack host seam,
// same as the Bridge — and now with the same source: the per-user appearance
// seat (ui_state 'ui_prefs', migration 026). This used to be a hardcoded
// 21-slot palette under the comment "until the console grows appearance
// prefs, the defaults ARE the prefs"; it has grown them.
//
// A GETTER, not a captured value: the seam is re-read per render, so a theme
// applied after boot (hydrate resolves a moment after first paint) is picked
// up without re-registering the host.
configurePiViewHost({
  activePalette: () => currentTheme().chat,
  terminalPrefs: () => defaultTerminalPrefs(),
});

const root = document.getElementById('app');

// Foundation seam (design-system-foundation): ONE delegated tooltip layer for
// every console view. Mounted ABOVE the router on the persistent #app root on
// purpose — home, users and audit all render inside it, so `data-tip` works
// on every page regardless of which downstream seam merges first, and a
// full-innerHTML view swap re-wires nothing (the listener delegates off this
// stable root). installTooltips returns its teardown; this mount deliberately
// outlives every view — one listener, one layer, nothing accumulates per
// navigation. The shared shell (shell.ts) deliberately leaves this here: its
// own lifetime is per-route, and the tooltip layer's is the app's.
if (root) installTooltips(root, { html: resolveRichTip });
// ...and the ONE confirm-destructive interaction (Styles × Themes PR-2),
// mounted with the same lifetime for the same reason: capture phase on the
// document, ahead of every delegated handler, so a destructive icon button
// arms before its action can fire and no call site has to know it exists.
installConfirmDestructive();
// ...and the scroll-rail SWEEPER (Styles × Themes PR-3c) — see
// rail-registry.ts. Same lifetime as the tooltip layer and for the same
// reason: every dialog, popover and menu here is built on open, so a
// one-shot sweep would rail the shell and nothing else.
installScrollRails(document, CONSOLE_RAILS);
// THE MENU FALLBACK (PR-3b). Attached on the document so no corner of this
// surface is left to the OS menu; the grid and recents attaches inside
// mountHome claim their own events first (the primitive skips an event whose
// default is already prevented), and editable text keeps the native menu.
mountCtxMenu();
attachCtxMenu(document);

/** The active view's teardown, if it returned one.
 *
 * mountHome installs document-level listeners and intervals; without this,
 * every hash navigation would leave another copy behind — a click handler that
 * fires N times and a ticker walking a DOM that is no longer on the page. */
let unmount: (() => void) | null = null;

function route(): void {
  if (!root) return;
  // Tear the previous view down BEFORE routing: mountHome installs document
  // listeners and a relative-time interval, and without this every hash
  // navigation would leave another copy behind.
  if (unmount) {
    try { unmount(); } catch { /* a bad teardown must not block the route */ }
    unmount = null;
  }
  // Two admin pages, each with its own nav entry (they were one combined
  // screen). #/admin is kept as a redirect so old links and bookmarks
  // still land somewhere real. EVERY mount's teardown is honoured now
  // (shared-shell): the admin pages used to return void, and the moment the
  // shell put the theme control and account menu on them — both document-
  // level listeners — an unhonoured admin teardown became one leaked copy
  // per navigation, the exact defect the home teardown existed to prevent.
  // #/help — the owner's muscle-memory URL (measured: it fell through to
  // mountHome, "just loads the main bridge screen"). The portal is a
  // SEPARATE document served at /help/, not a hash view in this client.
  if (location.hash.startsWith('#/help')) {
    location.href = '/help/';
    return;
  }
  if (location.hash.startsWith('#/users')) unmount = mountUsers(root);
  else if (location.hash.startsWith('#/audit')) unmount = mountAudit(root);
  // P20: the Brain page — the admin view of the organisation brain and its
  // gardener (runs, digest, entity page, proposals log, corrections through
  // the door). #/brain/entity/<id> routes inside the module.
  else if (location.hash.startsWith('#/brain')) unmount = mountBrain(root);
  // Configuration templates (owner item 65): the management list and the
  // per-template editor share one mount; the module routes on the id.
  else if (location.hash.startsWith('#/config-templates')) unmount = mountConfigTemplates(root);
  // API tokens (item 61): every signed-in role — reached from the account
  // menu, not the nav (it is an account surface, not a Bridge page).
  else if (location.hash.startsWith('#/tokens')) unmount = mountTokens(root);
  // #/api — muscle memory for the RETIRED in-shell docs page. The API
  // documentation portal is a SEPARATE document now (the /help/ shell
  // family, own window), same treatment as #/help above.
  else if (location.hash.startsWith('#/api')) { location.href = '/help/api/'; return; }
  else if (location.hash.startsWith('#/admin')) { location.hash = '#/users'; return; }
  // WS7: the Deployments tab (#/deployments/<release>[/<deployment>]).
  //
  // Work at a Glance USED to mount here, at #/glance/<preset>/<release>. It
  // never belonged on the home screen: the spec asked for a tab inside a
  // release ("In the helm-bridge UI we need a type of tab that shows the
  // things actively being worked on"), and the plan's own recipe was a
  // Bridge-client tab type. It now lives at #/glance/<preset> in the Bridge
  // client, reachable from the release's left rail. A stale bookmark to the
  // old home route lands on the releases list rather than a dead view — the
  // release it named is the thing you have to pick again anyway, and the
  // console home screen has no left nav or tab bar to host the tab.
  // Deployments moved to the Bridge client as a tab inside the release
  // (#/deployments there), for the same reason Work at a Glance did: the
  // owner asked for a tab in a release, and this home screen has no left nav
  // and no tab bar to host one. The console's deployment ROUTES are
  // untouched — they are the data layer the tab reads through /__console/*.
  // A stale bookmark lands on the releases list rather than a dead view: the
  // release it named is the thing you have to pick again anyway.
  else unmount = mountHome(root);

  // First-visit onboarding (item 63 PR 2): fire the surface's flow on its
  // first-ever visit (per-user ui_state seat); navigating to a different
  // surface mid-flow dismisses it. Only 'home' registers a flow today —
  // users/audit/tokens are admin/account pages, and the hook is a no-op for
  // surfaces with no flow.
  const surface = location.hash.startsWith('#/users') ? 'users'
    : location.hash.startsWith('#/audit') ? 'audit'
    : location.hash.startsWith('#/brain') ? 'brain'
    : location.hash.startsWith('#/tokens') ? 'tokens'
    // The templates route is TWO surfaces: the list and the editor
    // (#/config-templates/<id>) — each gets its own first-visit flow.
    : location.hash.startsWith('#/config-templates/') ? 'config-templates-editor'
    : location.hash.startsWith('#/config-templates') ? 'config-templates'
    : 'home';
  onboardingHook(surface);
}

/** One hook for the app's lifetime — created before the first route(). */
const onboardingHook = bootOnboarding();

window.addEventListener('hashchange', route);

// Boot: settle the per-user appearance seat BEFORE the first mount.
//
// This is awaited on purpose. The grid now reads its palette through var()
// strings resolved at PAINT time (gridThemeParams()), so it re-skins with the
// page — but the pi-view chat palette is still a VALUE handed over at render,
// and awaiting keeps the first mount from ever rendering a view against the
// default seat only to correct itself a beat later. Awaiting costs one
// same-origin GET against the console's own DB, and it costs nothing visually:
// index.html's pre-paint snippet has already painted the last-known theme from
// localStorage, so the page is correctly skinned the entire time.
//
// hydrate() never rejects — a 404 (no seat yet), a 401, or an offline browser
// all resolve to the cached-or-default seat, so the app always mounts.
// The icon sprite is injected ONCE, ahead of any consumer render: every icon
// on this page is `<use href="#i-<name>">`, and a `use` pointing at a symbol that
// is not in the document yet renders as nothing at all — silently, with no
// console error. Same contract as the Bridge's app.ts.
// STYLE-AWARE SPRITE (owner ruling 2026-09-06, supersedes #490 Blueprint-only):
// the chosen UI STYLE is unified across ALL surfaces, so the home composes the
// per-style icon set too — matching the Helm and the portals. This is no longer
// the "advertise a style you cannot render" half-state #490 removed: the home
// now carries the eight styles' MATERIAL tables (style-materials.css) and wires
// its chrome onto them, so a style switch moves BOTH the material and the icons.
// The sprite is read off the pre-paint-stamped `data-style` (appearance.ts
// gates that value through the dark-launch seat), and RE-INJECTED when the
// painted style changes — a seat change made in another tab/release lands here
// without a reload (onAppearanceChange fires on apply(), after the stamp).
function paintedStyleAttr(): string {
  return document.documentElement.getAttribute('data-style') ?? 'blueprint';
}
let injectedSprite = paintedStyleAttr();
injectIconSprite(injectedSprite);
// THE STYLE SUBSTRATE (owner correction 2026-09-07). Registers every canvas
// painter and resolves the reduce-effects seat BEFORE the first ground —
// applyStyleBackground() degrades a canvas style to `gradient` with only a
// console.warn when its painter is not registered yet, and that warning would
// be the only symptom. Then ground the pre-painted style immediately, so the
// layer element is in place before hydrate()'s apply() rather than one repaint
// later. Both lines LOG what they resolved: an unnamed runtime seam is how a
// style ships with its canvas silently unregistered.
console.log(bootStyleSubstrate(document.getElementById('app')));
groundStyle(injectedSprite);
console.log(describeGround(injectedSprite));
onAppearanceChange(() => {
  const next = paintedStyleAttr();
  if (next !== injectedSprite) {
    injectedSprite = next;
    injectIconSprite(next);
  }
});

void hydrate().then(() => {
  route();
  // ...and KEEP reading the seat. Appearance is one choice per user across the
  // home screen AND every release, so a theme changed inside a release must
  // land here without a reload. apply() rewrites the inline theme tokens, and
  // everything — AG Grid included, via its var() params — repaints in place.
  startAppearanceSync();
  // The assistant chat overlay (work item 64 PR 1) — the SAME pi-stack
  // implementation the Bridge client mounts, here with apiBase '' (apex
  // routes are bare). Mounted ABOVE the router on document.body, like the
  // tooltip layer: it must survive every hash navigation. Identity gates
  // the mount — a 401 (signed-out edge before the OIDC redirect lands)
  // simply never mounts it, the rail-account degradation rule.
  void fetch('/api/me')
    .then((r) => (r.ok ? (r.json() as Promise<{ id: string; email?: string; name?: string | null }>) : null))
    .then((me) => {
      if (me && me.id) {
        mountAssistantOverlay(document.body, {
          apiBase: '',
          user: me,
          // Screen-context DATA contract (work item 64 PR 3): the home
          // screen's identifiers only; hash/title basics ride along.
          contextProvider: () => ({ surface: 'bridge-home' }),
        });
      }
    })
    .catch(() => { /* offline — the home views surface that themselves */ });
});
